import React, { useState } from 'react';
import SimpleDemoModal from './SimpleDemoModal';
import { trackScheduleDemoClick } from '../utils/analytics';

const ProblemSolution: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const steps = [
    {
      number: "1",
      title: "Upload\nResumes",
      description: "Bulk upload or connect job postings. Vertex Find processes every profile in seconds.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      gradient: "from-blue-500 via-cyan-500 to-teal-500",
      pastelColor: "bg-blue-100",
      stats: "Instant processing",
      benefits: ["Bulk upload support", "Job posting integration", "Fast processing", "Multiple formats"]
    },
    {
      number: "2", 
      title: "AI Resume Analysis",
      description: "AI ranks and scores candidates by skills and relevance for an instant shortlist.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      gradient: "from-emerald-500 via-green-500 to-teal-500",
      pastelColor: "bg-emerald-100",
      stats: "Smart ranking",
      benefits: ["Skill matching", "Relevance scoring", "Instant shortlist", "AI-powered analysis"]
    },
    {
      number: "3",
      title: "AI Calls Candidates", 
      description: "AI agent phones shortlisted candidates to confirm interest and book interviews in your calendar. Fully customizable — define your own interview questions, and the AI asks them exactly as you specify.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      gradient: "from-purple-500 via-violet-500 to-indigo-500",
      pastelColor: "bg-purple-100",
      stats: "Automated calling",
      benefits: ["Direct phone calls", "Interest confirmation", "Calendar booking", "24/7 availability"]
    },
    {
      number: "4",
      title: "Focused Interviews",
      description: "Interview only qualified candidates who have confirmed their interest and availability.",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      gradient: "from-orange-500 via-red-500 to-pink-500",
      pastelColor: "bg-pink-100",
      stats: "Quality interviews",
      benefits: ["Pre-qualified candidates", "Confirmed interest", "Scheduled availability", "Higher success rate"]
    }
  ];

  return (
    <section id='about' className="pt-16 pb-24 md:pt-20 md:pb-32 bg-black relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="animate-fadeInUp">
            <p className="section-label">HOW IT WORKS</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
          </div>
          <div className="animate-fadeInUp" style={{ animationDelay: '0.1s' }}>
            <p className="text-base text-gray-300 max-w-2xl mx-auto">
              Four simple steps to transform your hiring process with AI automation 
              that works around the clock.
            </p>
          </div>
        </div>

        {/* Steps - Vertical Layout */}
        <div className="mb-10 max-w-4xl mx-auto">
          <div className="space-y-8">
            {steps.map((step, index) => {
              const colorClasses = [
                { cardBg: 'bg-blue-900/30', cardBorder: 'border-blue-600/50', badge: 'bg-blue-800 border-blue-600', text: 'text-blue-300', badgeText: 'text-blue-200', statsBg: 'bg-blue-800', statsBorder: 'border-blue-600' },
                { cardBg: 'bg-emerald-900/30', cardBorder: 'border-emerald-600/50', badge: 'bg-emerald-800 border-emerald-600', text: 'text-emerald-300', badgeText: 'text-emerald-200', statsBg: 'bg-emerald-800', statsBorder: 'border-emerald-600' },
                { cardBg: 'bg-purple-900/30', cardBorder: 'border-purple-600/50', badge: 'bg-purple-800 border-purple-600', text: 'text-purple-300', badgeText: 'text-purple-200', statsBg: 'bg-purple-800', statsBorder: 'border-purple-600' },
                { cardBg: 'bg-cyan-900/30', cardBorder: 'border-cyan-600/50', badge: 'bg-cyan-800 border-cyan-600', text: 'text-cyan-300', badgeText: 'text-cyan-200', statsBg: 'bg-cyan-800', statsBorder: 'border-cyan-600' },
              ];
              const colors = colorClasses[index];
              
              return (
                <div 
                  key={index}
                  className="animate-fadeInUp"
                  style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                >
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    {/* Number Badge */}
                    <div className="flex-shrink-0">
                      <div className={`w-16 h-16 rounded-xl ${colors.badge} flex items-center justify-center border-2 ${colors.statsBorder}`}>
                        <span className={`text-2xl font-bold ${colors.text}`}>{step.number}</span>
                      </div>
                    </div>

                    {/* Content Card */}
                    <div className="flex-1">
                      <div className={`p-6 md:p-8 rounded-2xl border-2 ${colors.cardBg} ${colors.cardBorder} transition-all duration-200 ease-out hover:border-opacity-100`}>
                        <h3 className="text-xl md:text-2xl font-semibold text-white mb-3 whitespace-pre-line">
                          {step.title}
                        </h3>
                        
                        <p className="text-gray-300 text-sm leading-relaxed mb-4">
                          {step.description}
                        </p>

                        {/* Stats Badge */}
                        <div className={`inline-flex items-center px-3 py-1.5 rounded-lg ${colors.statsBg} border ${colors.statsBorder}`}>
                          <span className={`text-xs font-medium ${colors.badgeText}`}>{step.stats}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA Section */}
        <div className="animate-fadeInUp" style={{ animationDelay: '0.5s' }}>
          <div className="p-6 md:p-8 text-center rounded-2xl border-2 border-white/20 transition-all duration-200 ease-out hover:border-white/30 hover:bg-[#ffffff22] bg-[#ffffff1a]">
            <div className="max-w-2xl mx-auto">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                Ready to Simplify Hiring?
              </h3>
              
              <p className="text-base text-gray-300 mb-6">
                Experience AI-powered automation that calls candidates for you, so you can focus on hiring great people.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
                <button 
                  className="btn-primary px-8 py-3 transition-all duration-200 hover:scale-105 active:scale-95"
                  onClick={() => {
                    trackScheduleDemoClick('problem_solution_section', { 
                      cta_type: 'section_cta_button',
                      section_name: 'how_it_works'
                    });
                    setIsModalOpen(true);
                  }}
                >
                  Schedule Demo →
                </button>
              </div>
              
              <p className="text-sm md:text-base text-gray-300 leading-relaxed">
                No credit card required • 5 day free trial • Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Modal */}
      <SimpleDemoModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)}
        modalType="demo"
      />
    </section>
  );
};

export default ProblemSolution; 